import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import { colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const CiroPayiPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_HR, user.effectivePermissions);

  const [ay, setAy] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [lokasyonId, setLokasyonId] = useState(4); // Altı Üstü default
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [ciro, setCiro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hesaplaLoading, setHesaplaLoading] = useState(false);
  const [editingKalem, setEditingKalem] = useState(null);
  const [editPuan, setEditPuan] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchLokasyonlar = async () => {
    try {
      const r = await fetch(`${API_URL}/api/lokasyonlar`, { headers });
      if (r.ok) setLokasyonlar((await r.json()).filter(l => l.aktif !== false));
    } catch (e) { logError(e); }
  };

  const fetchCiro = useCallback(async () => {
    if (!ay || !lokasyonId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/ciro-payi?ay=${ay}&lokasyon_id=${lokasyonId}`, { headers });
      if (r.ok) {
        const list = await r.json();
        setCiro(list.length > 0 ? list[0] : null);
      } else {
        setCiro(null);
      }
    } catch (e) { logError(e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ay, lokasyonId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLokasyonlar(); }, []);
  useEffect(() => { fetchCiro(); }, [fetchCiro]);

  const handleHesapla = async () => {
    if (!window.confirm(`${ay} için ciro payı hesaplanacak. Mevcut TASLAK silinip yeniden oluşturulacak. Devam?`)) return;
    setHesaplaLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/ciro-payi/hesapla`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ay: `${ay}-01`, lokasyon_id: parseInt(lokasyonId, 10) }),
      });
      if (r.ok) {
        toast.success('Hesaplandı');
        setCiro(await r.json());
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Hesaplanamadı');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
    setHesaplaLoading(false);
  };

  const handlePuanSave = async (kalemId) => {
    const puan = parseFloat(editPuan);
    if (isNaN(puan) || puan < 0) {
      toast.warning('Geçerli puan gir');
      return;
    }
    try {
      const r = await fetch(`${API_URL}/api/ciro-payi/kalem/${kalemId}/puan`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ puan }),
      });
      if (r.ok) {
        setCiro(await r.json());
        toast.success('Güncellendi');
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Güncellenemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
    setEditingKalem(null);
    setEditPuan('');
  };

  const handleKapat = async () => {
    if (!window.confirm('Ciro payı kapatılıp her personel için puantaj.ciro_payi güncellenecek. Emin misin?')) return;
    try {
      const r = await fetch(`${API_URL}/api/ciro-payi/${ciro.id}/kapat`, { method: 'POST', headers });
      if (r.ok) {
        setCiro(await r.json());
        toast.success('Kapatıldı ve puantajlara aktarıldı');
        invalidateQueries(['puantaj']);
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Kapatılamadı');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleAc = async () => {
    if (!window.confirm('KAPANDI → TASLAK çekilecek, TASLAK puantajlardaki ciro_payi sıfırlanacak.')) return;
    try {
      const r = await fetch(`${API_URL}/api/ciro-payi/${ciro.id}/ac`, { method: 'POST', headers });
      if (r.ok) {
        setCiro(await r.json());
        toast.success('Açıldı');
        invalidateQueries(['puantaj']);
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Açılamadı');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleSil = async () => {
    if (!window.confirm('Ciro payı kaydı silinecek.')) return;
    try {
      const r = await fetch(`${API_URL}/api/ciro-payi/${ciro.id}`, { method: 'DELETE', headers });
      if (r.ok) {
        toast.success('Silindi');
        setCiro(null);
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Silinemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const fmt = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtK = (n) => (n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

  const isLocked = ciro && ciro.durum === 'KAPANDI';

  if (!canManage) {
    return (
      <div>
        <Header title="Ciro Payı" />
        <div style={{ padding: 40, textAlign: 'center' }}>Yetkiniz yok.</div>
      </div>
    );
  }

  const servisKalemleri = ciro ? ciro.kalemler.filter(k => k.grup === 'SERVIS') : [];
  const mutfakKalemleri = ciro ? ciro.kalemler.filter(k => k.grup === 'MUTFAK') : [];

  const renderKalemTable = (title, havuz, toplamPuan, kalemler, groupColor) => (
    <div style={{ flex: 1, background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', background: groupColor, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{title}</strong>
        <span style={{ fontSize: 12 }}>
          Havuz: <strong>{fmtK(havuz)} ₺</strong> · Toplam Puan: <strong>{toplamPuan}</strong>
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#fafafa' }}>
            <th style={th}>Personel</th>
            <th style={th}>Pozisyon</th>
            <th style={{ ...th, textAlign: 'right' }}>Puan</th>
            <th style={{ ...th, textAlign: 'right' }}>Pay Tutarı</th>
          </tr>
        </thead>
        <tbody>
          {kalemler.length === 0 ? (
            <tr><td colSpan={4} style={{ padding: 20, textAlign: 'center', color: '#999' }}>Personel yok</td></tr>
          ) : kalemler.map(k => {
            const editing = editingKalem === k.id;
            return (
              <tr key={k.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={td}>{k.personel_ad}</td>
                <td style={td}>{k.pozisyon_kodu}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {editing ? (
                    <input
                      autoFocus
                      type="number"
                      step="0.5"
                      value={editPuan}
                      onChange={e => setEditPuan(e.target.value)}
                      onBlur={() => handlePuanSave(k.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handlePuanSave(k.id);
                        if (e.key === 'Escape') { setEditingKalem(null); setEditPuan(''); }
                      }}
                      style={{ width: 60, padding: 3, fontSize: 12, border: '2px solid #1565c0', borderRadius: 3 }}
                    />
                  ) : (
                    <span
                      onClick={() => { if (!isLocked) { setEditingKalem(k.id); setEditPuan(k.puan); } }}
                      style={{ cursor: isLocked ? 'not-allowed' : 'pointer', color: isLocked ? '#999' : '#1565c0', fontWeight: 600 }}
                    >{k.puan}</span>
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: '#2e7d32' }}>{fmt(k.pay_tutari)}</td>
              </tr>
            );
          })}
        </tbody>
        {kalemler.length > 0 && (
          <tfoot>
            <tr style={{ background: '#fafafa', fontWeight: 700 }}>
              <td style={td} colSpan={2}>TOPLAM</td>
              <td style={{ ...td, textAlign: 'right' }}>{toplamPuan}</td>
              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(kalemler.reduce((s, k) => s + (k.pay_tutari || 0), 0))} ₺</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );

  return (
    <div>
      <Header title="Ciro Payı" />
      <div style={{ padding: 20, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {/* Filtre bar */}
        <div style={{ background: colors.surface, padding: 14, borderRadius: radius.lg, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', boxShadow: shadows.sm }}>
          <input type="month" value={ay} onChange={e => setAy(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14 }} />
          <select value={lokasyonId} onChange={e => setLokasyonId(e.target.value)} style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14 }}>
            {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
          </select>
          <button onClick={handleHesapla} disabled={hesaplaLoading} style={{ padding: '9px 18px', background: '#7b1fa2', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600 }}>
            {hesaplaLoading ? 'Hesaplanıyor...' : 'Hesapla / Yenile'}
          </button>
          {ciro && (
            <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: ciro.durum === 'KAPANDI' ? '#e8f5e9' : '#fff3e0', color: ciro.durum === 'KAPANDI' ? '#2e7d32' : '#e65100' }}>
              {ciro.durum}
            </span>
          )}
        </div>

        {loading && <div style={{ padding: 20, textAlign: 'center' }}>Yükleniyor...</div>}

        {!loading && !ciro && (
          <div style={{ padding: 40, textAlign: 'center', color: '#888', background: colors.surface, borderRadius: radius.md }}>
            Bu ay için ciro payı hesabı yok. "Hesapla" butonuna basın.
          </div>
        )}

        {ciro && (
          <>
            {/* KPI */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
              <div style={{ ...kpiCard, borderLeft: '4px solid #1565c0' }}>
                <div style={kpiLabel}>Aylık Ciro (POS)</div>
                <div style={kpiValue}>{fmtK(ciro.aylik_ciro)} ₺</div>
              </div>
              <div style={{ ...kpiCard, borderLeft: '4px solid #6a1b9a' }}>
                <div style={kpiLabel}>Havuz Toplam</div>
                <div style={kpiValue}>{fmtK(ciro.havuz_toplam)} ₺</div>
                <div style={kpiSub}>Ciro × %6</div>
              </div>
              <div style={{ ...kpiCard, borderLeft: '4px solid #2e7d32' }}>
                <div style={kpiLabel}>Servis Havuzu</div>
                <div style={kpiValue}>{fmtK(ciro.servis_havuz)} ₺</div>
                <div style={kpiSub}>%55 · {ciro.servis_toplam_puan} puan</div>
              </div>
              <div style={{ ...kpiCard, borderLeft: '4px solid #e65100' }}>
                <div style={kpiLabel}>Mutfak Havuzu</div>
                <div style={kpiValue}>{fmtK(ciro.mutfak_havuz)} ₺</div>
                <div style={kpiSub}>%45 · {ciro.mutfak_toplam_puan} puan</div>
              </div>
            </div>

            {/* İki tablo yan yana */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
              {renderKalemTable('SERVİS', ciro.servis_havuz, ciro.servis_toplam_puan, servisKalemleri, '#2e7d32')}
              {renderKalemTable('MUTFAK', ciro.mutfak_havuz, ciro.mutfak_toplam_puan, mutfakKalemleri, '#e65100')}
            </div>

            {/* Aksiyon bar */}
            <div style={{ background: colors.surface, padding: 14, borderRadius: radius.md, boxShadow: shadows.sm, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              {ciro.durum === 'TASLAK' && (
                <>
                  <button onClick={handleSil} style={{ padding: '9px 18px', background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600 }}>Sil</button>
                  <button onClick={handleKapat} style={{ padding: '9px 20px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 700 }}>Kapat ve Puantaja Aktar</button>
                </>
              )}
              {ciro.durum === 'KAPANDI' && (
                <button onClick={handleAc} style={{ padding: '9px 20px', background: '#fbc02d', color: '#333', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600 }}>Aç (Yönetim)</button>
              )}
            </div>

            {isLocked && (
              <div style={{ marginTop: 10, padding: 10, background: '#fffde7', borderLeft: '3px solid #fbc02d', fontSize: 12, color: '#555' }}>
                Bu ay ciro payı <strong>KAPANDI</strong> — puan değişiklikleri için önce "Aç" butonuna basın.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const th = { padding: '6px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#555', borderBottom: '2px solid #ddd' };
const td = { padding: '6px 8px', fontSize: 11 };
const kpiCard = { background: '#fff', padding: 14, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const kpiLabel = { fontSize: 11, color: '#666', marginBottom: 4 };
const kpiValue = { fontSize: 20, fontWeight: 700, color: '#333' };
const kpiSub = { fontSize: 10, color: '#888', marginTop: 2 };

export default CiroPayiPage;
